/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Drop-in factory mirroring the subset of `@contentauth/c2pa-web`'s `createC2pa`
 * that @nettrek/c2pa-hls-bridge uses: `{ reader: { fromBlob, fromBlobFragment },
 * dispose }`. No WASM, no worker — verification runs on WebCrypto in-thread.
 */

import { WebCryptoReader, type Reader } from './reader/Reader'
import { runVerification } from './verify/verify'

/** Configuration accepted for API compatibility. `wasmSrc` is ignored (no WASM). */
export interface Config {
    wasmSrc?: string | unknown
    settings?: Settings
}

/** Verification/trust settings, structurally compatible with c2pa-web's `Settings`. */
export interface Settings {
    trust?: TrustSettings
    cawgTrust?: TrustSettings
    /**
     * `verifyTrust` gates chain-to-anchor / allow-list trust evaluation. `verifyAfterReading`
     * is accepted for c2pa-web API compatibility but ignored — this reader always verifies.
     */
    verify?: { verifyTrust?: boolean, verifyAfterReading?: boolean }
    [k: string]: unknown
}

export interface TrustSettings {
    trustAnchors?: string | string[]
    /** Accepted for c2pa-web API compatibility but currently ignored (only `trustAnchors` is consumed). */
    userAnchors?: string | string[]
    trustConfig?: string | string[]
    allowedList?: string | string[]
    [k: string]: unknown
}

export interface ReaderFactory {
    fromBlob: (format: string, blob: Blob, settings?: Settings) => Promise<Reader | null>
    fromBlobFragment: (format: string, init: Blob, fragment: Blob, settings?: Settings) => Promise<Reader | null>
}

export interface C2paSdk {
    reader: ReaderFactory
    dispose: () => void
}

async function blobBytes (blob: Blob): Promise<Uint8Array> {
    return new Uint8Array(await blob.arrayBuffer())
}

/** Creates a WebCrypto-backed C2PA SDK instance. */
export async function createC2pa (config: Config = {}): Promise<C2paSdk> {
    const baseSettings = config.settings

    async function build (init: Uint8Array, fragment: Uint8Array | undefined, settings: Settings | undefined): Promise<Reader | null> {
        // Backstop: malformed / truncated / adversarial input must never crash the
        // reader. Individual parsers degrade to Invalid / diagnostics where they can;
        // this guard catches anything that still throws (e.g. a corrupt structure a
        // parser did not anticipate) and returns null — "no verifiable manifest" —
        // rather than propagating out of the public fromBlob/fromBlobFragment API.
        try {
            const reader = WebCryptoReader.create({ init, fragment })
            if (!reader) return null
            await runVerification(reader, { ...baseSettings, ...settings })
            return reader
        } catch {
            return null
        }
    }

    return {
        reader: {
            async fromBlob (_format, blob, settings) {
                return build(await blobBytes(blob), undefined, settings)
            },
            async fromBlobFragment (_format, init, fragment, settings) {
                return build(await blobBytes(init), await blobBytes(fragment), settings)
            }
        },
        dispose () { /* nothing to release */ }
    }
}
